import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Brain, Target, Users, Send, X, GraduationCap, CheckCircle2, XCircle, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import GoalsView from './GoalsView';

const CATEGORY_LABELS = {
  leadership: 'Leadership',
  finance: 'Finance & Investing',
  history: 'History & World Events',
  science: 'Science & Tech',
  philosophy: 'Philosophy & Ethics',
  parenting: 'Parenting & Child Dev',
  health: 'Health & Wellness',
  language_learning: 'Language Learning',
};

const DIFFICULTY_LABELS = {
  explorer: 'Explorer', adventurer: 'Adventurer', scholar: 'Scholar', champion: 'Champion', genius: 'Genius',
};

// --- Parent Quiz card ---
function ParentQuiz() {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [difficulty, setDifficulty] = useState('');
  const [category, setCategory] = useState('');
  const [done, setDone] = useState(false);

  const start = async () => {
    setLoading(true); setDone(false);
    try {
      const params = {};
      if (difficulty) params.difficulty = difficulty;
      if (category) params.category = category;
      const { data } = await api.get('/parent-quiz/daily', { params });
      setQuestions(data.questions || []);
      setIdx(0); setSelected(null); setResult(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load your challenge'); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (!selected) return;
    const q = questions[idx];
    try {
      const { data } = await api.post('/quiz/submit', { question_id: q.id, answer: selected });
      setResult(data);
      if (data.is_correct) toast.success('Nailed it!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
  };

  const next = () => {
    setSelected(null); setResult(null);
    if (idx + 1 < questions.length) setIdx(idx + 1);
    else setDone(true);
  };

  const q = questions[idx];
  const started = questions.length > 0 && !done;

  return (
    <section className="bg-gradient-to-br from-navy via-teal to-navy rounded-3xl p-6 text-white shadow-squishyLg" data-testid="parent-quiz-card">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Brain className="w-7 h-7 text-gold" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-gold text-[11px] font-heading font-bold uppercase tracking-wider">Your Daily Growth Challenge</div>
          <h2 className="font-heading text-2xl font-bold leading-tight">5 questions. 3 minutes. One sharper you.</h2>
        </div>
      </div>

      {!started && !done && (
        <>
          <div className="mb-3">
            <div className="text-white/80 text-[10px] font-heading font-bold uppercase tracking-wider mb-1.5">Category (optional)</div>
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setCategory('')} className={`px-3 py-1 rounded-full text-xs font-heading font-bold border-2 ${!category ? 'bg-gold text-navy border-gold' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`} data-testid="parent-cat-any">Any</button>
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                <button key={k} onClick={() => setCategory(k)} className={`px-3 py-1 rounded-full text-xs font-heading font-bold border-2 ${category === k ? 'bg-gold text-navy border-gold' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`} data-testid={`parent-cat-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <div className="text-white/80 text-[10px] font-heading font-bold uppercase tracking-wider mb-1.5">Difficulty (optional)</div>
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setDifficulty('')} className={`px-3 py-1 rounded-full text-xs font-heading font-bold border-2 ${!difficulty ? 'bg-gold text-navy border-gold' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`} data-testid="parent-diff-any">Any</button>
              {Object.entries(DIFFICULTY_LABELS).map(([k, l]) => (
                <button key={k} onClick={() => setDifficulty(k)} className={`px-3 py-1 rounded-full text-xs font-heading font-bold border-2 ${difficulty === k ? 'bg-gold text-navy border-gold' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`} data-testid={`parent-diff-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <button onClick={start} disabled={loading}
            className="w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="start-parent-quiz">
            <Sparkles className="w-4 h-4" /> {loading ? 'Loading…' : 'Start challenge'}
          </button>
        </>
      )}

      {started && q && (
        <div className="bg-white text-navy rounded-2xl p-4" data-testid="parent-quiz-question">
          <div className="text-[10px] font-heading font-bold text-teal uppercase tracking-wider mb-1">
            {CATEGORY_LABELS[q.category] || q.category}{q.difficulty ? ` · ${DIFFICULTY_LABELS[q.difficulty] || q.difficulty}` : ''} · {idx + 1}/{questions.length}
          </div>
          <h3 className="font-heading text-lg font-bold mb-3">{q.question}</h3>
          <div className="space-y-2">
            {q.options.map((opt) => {
              const isSel = selected === opt;
              const isCorrect = result && opt === result.correct_answer;
              const isWrong = result && isSel && opt !== result.correct_answer;
              let cls = 'border-slate-200 hover:border-teal';
              if (isSel && !result) cls = 'border-teal bg-teal/10';
              if (isCorrect) cls = 'border-teal bg-teal/15';
              if (isWrong) cls = 'border-red-400 bg-red-50';
              return (
                <button key={opt} onClick={() => !result && setSelected(opt)} disabled={!!result}
                  className={`w-full flex items-center justify-between gap-2 p-3 rounded-xl border-2 text-left ${cls}`}
                  data-testid={`parent-quiz-option-${opt.replace(/\s+/g,'-').toLowerCase()}`}>
                  <span className="text-sm font-semibold">{opt}</span>
                  {isCorrect && <CheckCircle2 className="w-4 h-4 text-teal" />}
                  {isWrong && <XCircle className="w-4 h-4 text-red-500" />}
                </button>
              );
            })}
          </div>
          {result && q.fun_fact && (
            <div className="mt-3 p-3 rounded-xl bg-cream text-xs" data-testid="parent-quiz-fun-fact">
              <span className="font-heading font-bold text-teal">Did you know?</span> {q.fun_fact}
            </div>
          )}
          <div className="mt-3">
            {!result ? (
              <button onClick={submit} disabled={!selected}
                className="w-full h-11 rounded-full bg-navy text-white font-heading font-bold text-sm disabled:opacity-40"
                data-testid="parent-quiz-submit">Submit</button>
            ) : (
              <button onClick={next} className="w-full h-11 rounded-full bg-gold text-navy font-heading font-bold text-sm shadow-gold" data-testid="parent-quiz-next">
                {idx + 1 < questions.length ? 'Next question' : 'Finish challenge'}
              </button>
            )}
          </div>
        </div>
      )}

      {done && (
        <div className="bg-white text-navy rounded-2xl p-4 text-center" data-testid="parent-quiz-done">
          <div className="text-4xl mb-1">🎉</div>
          <p className="font-heading font-bold">Nice work — challenge complete.</p>
          <button onClick={() => { setQuestions([]); setDone(false); }} className="mt-3 px-4 py-2 rounded-full bg-navy text-white font-heading font-bold text-xs">Do another</button>
        </div>
      )}
    </section>
  );
}

// --- Parent Tutor mini-chat modal ---
function ParentTutorModal({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(`parent-tutor-${Date.now()}`);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setSending(true);
    try {
      const { data } = await api.post('/tutor/chat', { text, session_id: sessionId });
      setMessages((m) => [...m, { role: 'tutor', text: data.text }]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Tutor failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="parent-tutor-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg h-[80vh] flex flex-col shadow-squishyLg overflow-hidden">
        <div className="bg-gradient-to-br from-navy to-teal p-4 text-white flex items-center justify-between">
          <div>
            <div className="text-gold text-[10px] font-heading font-bold uppercase tracking-wider">Socratic Tutor</div>
            <h2 className="font-heading text-lg font-bold">What are you curious about today?</h2>
          </div>
          <button onClick={onClose} data-testid="close-parent-tutor"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="parent-tutor-messages">
          {messages.length === 0 && (
            <div className="text-center text-navy/60 text-sm p-4">
              Work, the world, or something else — I'm ready if you are.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-navy text-white' : 'bg-cream text-navy'}`}>{m.text}</div>
            </div>
          ))}
          {sending && <div className="text-navy/50 text-xs">Tutor is thinking…</div>}
        </div>
        <div className="p-3 border-t-2 border-cream flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask anything…"
            className="flex-1 h-11 px-3 rounded-full border-2 border-slate-200 focus:border-teal focus:outline-none text-sm"
            data-testid="parent-tutor-input" />
          <button onClick={send} disabled={sending} className="w-11 h-11 rounded-full bg-gold text-navy font-heading font-bold disabled:opacity-60" data-testid="parent-tutor-send"><Send className="w-4 h-4 mx-auto" /></button>
        </div>
      </div>
    </div>
  );
}

// --- Family Dinner Question ---
function DinnerQuestion() {
  const [data, setData] = useState({ question: '', answers: [] });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get('/family/dinner-answers');
      setData(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post('/family/dinner-answer', { text: text.trim() });
      toast.success('Answer shared with your family');
      setText('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const myAnswer = data.answers.find((a) => a.user_id === user?.id);
  const others = data.answers.filter((a) => a.user_id !== user?.id);

  return (
    <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="dinner-question-card">
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle className="w-5 h-5 text-teal" />
        <h3 className="font-heading text-lg font-bold text-navy">Dinner Table Question</h3>
      </div>
      <p className="text-navy text-base font-heading font-bold leading-snug mb-3" data-testid="dinner-question-text">{data.question}</p>
      {myAnswer ? (
        <div className="rounded-2xl bg-teal/10 border-2 border-teal p-3 mb-3">
          <div className="text-[10px] font-heading font-bold text-teal uppercase mb-1">Your answer</div>
          <div className="text-sm text-navy">{myAnswer.text}</div>
        </div>
      ) : (
        <div className="mb-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Share your answer with your family…"
            rows={3} maxLength={600}
            className="w-full p-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none text-sm"
            data-testid="dinner-answer-input" />
          <button onClick={submit} disabled={busy || !text.trim()}
            className="mt-2 px-4 py-2 rounded-full bg-navy text-white font-heading font-bold text-xs disabled:opacity-60"
            data-testid="dinner-answer-submit">{busy ? 'Sharing…' : 'Share my answer'}</button>
        </div>
      )}
      {others.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-heading font-bold text-navy/60 uppercase tracking-wider">Your family said</div>
          {others.map((a) => (
            <div key={a.id} className="rounded-2xl bg-cream p-3" data-testid={`dinner-family-${a.id}`}>
              <div className="text-[10px] font-heading font-bold text-teal uppercase mb-0.5">{a.user_name} · {a.user_role}</div>
              <div className="text-sm text-navy">{a.text}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// --- Main Parent Learn tab ---
export default function ParentLearn() {
  const [showTutor, setShowTutor] = useState(false);
  return (
    <div className="space-y-5" data-testid="parent-learn-tab">
      <ParentQuiz />

      <section className="rounded-3xl p-5 bg-gradient-to-br from-gold/25 to-teal/25 border-2 border-gold" data-testid="parent-tutor-card">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-navy flex items-center justify-center shrink-0"><GraduationCap className="w-6 h-6 text-gold" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-navy/60 text-[10px] font-heading font-bold uppercase tracking-wider">Socratic Tutor for adults</div>
            <h3 className="font-heading text-lg font-bold text-navy">Ask anything — career, world, or a skill you want next</h3>
            <p className="text-navy/70 text-xs mt-1">Peer-to-peer. Intellectual. Curious. Not condescending.</p>
            <button onClick={() => setShowTutor(true)}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white font-heading font-bold text-sm"
              data-testid="open-parent-tutor"><Sparkles className="w-3.5 h-3.5" /> Start a session</button>
          </div>
        </div>
      </section>

      <section data-testid="parent-goals-section">
        <div className="flex items-center gap-2 mb-3 px-1">
          <Target className="w-5 h-5 text-teal" />
          <h3 className="font-heading text-lg font-bold text-navy">Your goals</h3>
        </div>
        <GoalsView title="Your goals" />
      </section>

      <DinnerQuestion />

      {showTutor && <ParentTutorModal onClose={() => setShowTutor(false)} />}
    </div>
  );
}
